import React, { useState } from 'react';
import { Box, Divider, InputAdornment, MenuItem } from '@material-ui/core';
import styled from 'styled-components';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import {
    CompanyDocument,
    ICompanyQuery,
    ILocaleChoices,
    IUserType,
    UserDocument,
    useUserCreateMutation,
    useUserDeleteMutation,
    useUserUpdateMutation,
} from 'graphql/types';
import { FormField } from 'common/components/FormField';
import { TextField } from 'common/components/TextField';
import { ButtonLink } from 'common/components/buttons/ButtonLink';
import { Loader } from 'common/components/Loader';
import { ISnackbarState } from 'common/interfaces/snackbarState';
import { getFullPhone, getPhoneValue } from 'common/utils';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { validators } from 'common/constants';
import { Select } from 'common/components/Select';

type TInitialValues = {
    id: string;
    firstName: string;
    lastNamePrefix: string;
    lastName: string;
    jobTitle: string;
    email: string;
    phone: string;
    locale: ILocaleChoices;
};

interface IProps {
    canDelete?: boolean;
    className?: string;
    user?: Pick<
        IUserType,
        | 'id'
        | 'firstName'
        | 'lastNamePrefix'
        | 'lastName'
        | 'email'
        | 'jobTitle'
        | 'phone'
        | 'locale'
    > &
        Partial<Pick<IUserType, 'isMainUser'>>;
    onClose(snackbarState?: ISnackbarState, refetch?: boolean): void;
}

export const BaseUserForm = ({
    canDelete,
    className,
    user,
    onClose,
}: IProps) => {
    const { t } = useTranslation();
    const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
    const [createUser, { loading: loadingCreate }] = useUserCreateMutation({
        update: (cache, { data: newUserData }) => {
            const data = cache.readQuery({
                query: CompanyDocument,
            }) as ICompanyQuery;

            if (data && newUserData) {
                cache.modify({
                    id: cache.identify(data.company),
                    fields: {
                        users(existingItems = []) {
                            return [...existingItems, newUserData.createUser];
                        },
                    },
                });
            }

            onClose({
                message: t('messages.userCreated'),
                variant: 'success',
            });
        },
        onError: (error) => {
            onClose({
                message: error.message,
                variant: 'error',
            });
        },
    });
    const [updateUser, { loading: loadingUpdate }] = useUserUpdateMutation({
        update: () => {
            onClose({
                message: t('messages.changesSaved'),
                variant: 'success',
            });
        },
        onError: (error) => {
            onClose({
                message: error.message,
                variant: 'error',
            });
        },
        refetchQueries: [{ query: UserDocument }, { query: CompanyDocument }],
    });
    const [deleteUser] = useUserDeleteMutation({
        update: () => {
            onClose(
                {
                    message: t('messages.userDeleted'),
                    variant: 'success',
                },
                true
            );
        },
        onError: (error) => {
            onClose({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const {
        id,
        firstName,
        lastNamePrefix,
        lastName,
        jobTitle,
        email,
        phone,
        isMainUser,
        locale,
    } = user || {};

    const formik = useFormik({
        initialValues: {
            id: id || '',
            firstName: firstName || '',
            lastNamePrefix: lastNamePrefix || '',
            lastName: lastName || '',
            jobTitle: jobTitle || '',
            email: email || '',
            phone: phone ? getPhoneValue(phone) : '',
            locale: locale || ILocaleChoices.NlBe,
        },
        validationSchema: Yup.object().shape({
            firstName: validators.firstName,
            lastName: validators.lastName,
            jobTitle: validators.jobTitle,
            email: validators.email,
            phone: validators.phone,
        }),
        onSubmit: (values) => {
            if (!user) {
                const newValues = { ...values, id: undefined };

                createUser({
                    variables: {
                        userData: {
                            ...newValues,
                            phone: getFullPhone(newValues.phone),
                        },
                    },
                });

                return;
            }

            const { email, ...updatedValues } = values;

            updateUser({
                variables: {
                    userData: {
                        ...updatedValues,
                        phone: getFullPhone(values.phone),
                    },
                },
            });
        },
    });

    const {
        errors: formikErrors,
        touched,
        values,
        handleChange,
        handleSubmit,
    } = formik;

    const getFieldError = (fieldName: keyof TInitialValues) => {
        if (touched[fieldName] && formikErrors[fieldName]) {
            return formikErrors[fieldName];
        }

        return '';
    };

    const handleDeleteUser = () => {
        setShowRemoveUserModal(false);

        if (!user) return;

        deleteUser({ variables: { id: user.id } });
    };

    return (
        <Box className={className}>
            <ConfirmDialog
                cancelProps={{
                    onClick: () => setShowRemoveUserModal(false),
                }}
                confirmProps={{
                    text: t('dialogs.removeUser.confirm'),
                    onClick: handleDeleteUser,
                }}
                open={showRemoveUserModal}
                title={t('dialogs.removeUser.title')}
            >
                {t('dialogs.removeUser.message')}
            </ConfirmDialog>

            <form onSubmit={handleSubmit}>
                <Box className="nameRowWrapper">
                    <Box className="firstNameColumn">
                        <FormField>
                            <TextField
                                error={getFieldError('firstName')}
                                id="firstName"
                                label={t('formfields.firstName')}
                                value={values.firstName}
                                variant="filled"
                                onChange={handleChange}
                            />
                        </FormField>
                    </Box>
                    <Box className="lastNamePrefixColumn">
                        <FormField>
                            <TextField
                                error={getFieldError('lastNamePrefix')}
                                id="lastNamePrefix"
                                label={t('formfields.lastNamePrefix')}
                                value={values.lastNamePrefix}
                                variant="filled"
                                onChange={handleChange}
                            />
                        </FormField>
                    </Box>
                </Box>
                <FormField>
                    <TextField
                        error={getFieldError('lastName')}
                        id="lastName"
                        label={t('formfields.lastName')}
                        value={values.lastName}
                        variant="filled"
                        onChange={handleChange}
                    />
                </FormField>
                <FormField>
                    <TextField
                        error={getFieldError('jobTitle')}
                        id="jobTitle"
                        label={t('formfields.jobTitle')}
                        value={values.jobTitle}
                        variant="filled"
                        onChange={handleChange}
                    />
                </FormField>
                <FormField>
                    <TextField
                        disabled={!!user}
                        error={getFieldError('email')}
                        id="email"
                        label={t('formfields.email')}
                        type="email"
                        value={values.email}
                        variant="filled"
                        onChange={handleChange}
                    />
                </FormField>
                <FormField>
                    <TextField
                        phone
                        error={getFieldError('phone')}
                        id="phone"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    +32 4{' '}
                                </InputAdornment>
                            ),
                        }}
                        label={t('formfields.phone')}
                        value={getPhoneValue(values.phone)}
                        variant="filled"
                        onChange={handleChange}
                    />
                </FormField>
                <FormField>
                    <Select
                        id="locale"
                        label="Taal"
                        name="locale"
                        value={values.locale}
                        onChange={handleChange}
                    >
                        <MenuItem value={ILocaleChoices.NlBe}>
                            Nederlands
                        </MenuItem>
                        <MenuItem value={ILocaleChoices.FrBe}>Frans</MenuItem>
                    </Select>
                </FormField>
            </form>

            <Box mb={8} mt={8}>
                <Divider />
            </Box>

            {loadingCreate || loadingUpdate ? (
                <Loader />
            ) : (
                <Box display="flex" flexDirection="column">
                    <Box mb={3}>
                        <ButtonLink
                            icon={<ArrowForwardIcon />}
                            onClick={() => handleSubmit()}
                        >
                            {!user
                                ? t('user.buttonCreate')
                                : t('user.buttonSaveChanges')}
                        </ButtonLink>
                    </Box>

                    {canDelete && !isMainUser && (
                        <Box mb={3}>
                            <ButtonLink
                                icon={<ArrowForwardIcon />}
                                onClick={() => setShowRemoveUserModal(true)}
                            >
                                {t('user.buttonDelete')}
                            </ButtonLink>
                        </Box>
                    )}

                    <ButtonLink
                        cancel
                        icon={<ArrowForwardIcon />}
                        onClick={() => onClose()}
                    >
                        {t('user.buttonCancel')}
                    </ButtonLink>
                </Box>
            )}
        </Box>
    );
};

export const UserForm = styled(BaseUserForm)`
    form {
        width: 100%;
        max-width: 455px;
    }

    .leftSection {
        margin-bottom: ${({ theme }) => `${theme.spacing(4)}px`};
    }

    .firstNameColumn,
    .lastNamePrefixColumn {
        width: 100%;
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['sm']}px`}) {
        .nameRowWrapper {
            display: flex;
        }

        .firstNameColumn {
            width: 60%;
            margin-right: ${({ theme }) => `${theme.spacing(4)}px`};
        }

        .lastNamePrefixColumn {
            width: 40%;
        }
    }
`;
